import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Left accent bar colour (used by KPI tiles). */
  accent?: 'blue' | 'green' | 'teal' | 'cyan' | 'danger' | 'warning';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Subtle brand gradient wash (header cards, plan card). */
  tint?: boolean;
  interactive?: boolean;
}

const accents = {
  blue: 'before:bg-brand-blue-2',
  green: 'before:bg-success',
  teal: 'before:bg-brand-teal',
  cyan: 'before:bg-brand-cyan',
  danger: 'before:bg-danger',
  warning: 'before:bg-warning',
};

const paddings = { none: '', sm: 'p-4', md: 'p-6', lg: 'p-8' };

export function Card({ accent, padding = 'md', tint, interactive, className, children, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        'relative rounded-card border border-border bg-surface',
        paddings[padding],
        tint && 'surface-tint',
        interactive && 'transition-colors hover:border-border-strong cursor-pointer',
        accent &&
          cn(
            'overflow-hidden before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:content-[""]',
            accents[accent],
          ),
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface SectionCardProps extends Omit<CardProps, 'title'> {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned slot in the header (links, segmented controls, legends). */
  extra?: ReactNode;
  bodyClassName?: string;
}

export function SectionCard({ title, subtitle, extra, bodyClassName, children, className, ...rest }: SectionCardProps) {
  return (
    <Card className={cn('flex flex-col', className)} {...rest}>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[17px] font-semibold leading-tight text-text">{title}</h3>
          {subtitle && <p className="mt-1 text-[13px] text-muted">{subtitle}</p>}
        </div>
        {extra && <div className="flex items-center gap-2 text-[13px] text-muted">{extra}</div>}
      </div>
      <div className={cn('min-w-0 flex-1', bodyClassName)}>{children}</div>
    </Card>
  );
}
